import { PostData } from "./FetchData";
import {
	DEPLOY_JOB_POLL_MS,
	normalizeDeployJob,
	normalizeLaterJob,
	normalizeVerification,
	parseDeployResponse,
	pollDeployJobWith,
	requireWebsiteId,
	toJobId,
	isDeployAwaitingReview,
	isDeployJobFailed,
	isDeployTerminal,
} from "./deployApiShared";

const MS1 = "ms1";
const BASE = "api/v1/website";

export {
	DEPLOY_JOB_POLL_MS,
	normalizeDeployJob,
	normalizeVerification,
	isDeployJobFailed,
	isDeployAwaitingReview,
	isDeployTerminal,
};

/**
 * Start deploy pipeline from a writing-assistant change.
 */
export async function deployFromWriting({
	websiteId,
	writingJobId,
	url,
	changeIndex,
}) {
	const missing = requireWebsiteId(websiteId);
	if (missing) return { ...missing, jobId: null };
	if (writingJobId == null || writingJobId === "") {
		return { ok: false, message: "writing_job_id is required", jobId: null };
	}
	const res = await PostData(MS1, `${BASE}/wordpressDeployFromWriting`, {
		websiteId: Number(websiteId),
		writing_job_id: Number(writingJobId),
		url: url || "",
		change_index: Number(changeIndex),
	});
	const parsed = parseDeployResponse(res, "Failed to start WordPress deploy");
	if (!parsed.ok) return { ...parsed, jobId: null };
	const data = parsed.data || {};
	const jobId = data.job_id ?? data.jobId ?? data.id ?? null;
	return {
		ok: true,
		message: parsed.message || data.message,
		jobId,
		data: normalizeDeployJob({ ...data, id: jobId }),
	};
}

export async function getDeployJob(websiteId, jobId) {
	const missing = requireWebsiteId(websiteId);
	if (missing) return { ...missing, job: null };
	if (jobId == null || jobId === "") {
		return { ok: false, message: "jobId is required", job: null };
	}
	const res = await PostData(MS1, `${BASE}/wordpressGetDeployJob`, {
		websiteId: Number(websiteId),
		jobId: toJobId(jobId),
	});
	const parsed = parseDeployResponse(res, "Failed to load deploy job");
	if (!parsed.ok) return { ...parsed, job: null };
	return {
		ok: true,
		message: parsed.message,
		job: normalizeDeployJob(parsed.data),
	};
}

export async function listDeployJobs(websiteId) {
	const missing = requireWebsiteId(websiteId);
	if (missing) return { ...missing, jobs: [] };
	const res = await PostData(MS1, `${BASE}/wordpressListDeployJobs`, {
		websiteId: Number(websiteId),
	});
	const parsed = parseDeployResponse(res, "Failed to list deploy jobs");
	if (!parsed.ok) return { ...parsed, jobs: [] };
	const rawList = Array.isArray(parsed.data)
		? parsed.data
		: Array.isArray(parsed.data?.jobs)
			? parsed.data.jobs
			: [];
	return {
		ok: true,
		message: parsed.message,
		jobs: rawList.map(normalizeDeployJob).filter(Boolean),
	};
}

export async function getDeployVerification(websiteId, jobId) {
	const missing = requireWebsiteId(websiteId);
	if (missing) return { ...missing, verification: null };
	if (jobId == null || jobId === "") {
		return { ok: false, message: "jobId is required", verification: null };
	}
	const res = await PostData(MS1, `${BASE}/wordpressGetVerification`, {
		websiteId: Number(websiteId),
		jobId: toJobId(jobId),
	});
	const parsed = parseDeployResponse(res, "Failed to load verification");
	if (!parsed.ok) return { ...parsed, verification: null };
	return {
		ok: true,
		message: parsed.message,
		verification: normalizeVerification(parsed.data),
	};
}

export async function discardDeployJob(websiteId, jobId) {
	const missing = requireWebsiteId(websiteId);
	if (missing) return missing;
	if (jobId == null || jobId === "") {
		return { ok: false, message: "jobId is required", data: null };
	}
	const res = await PostData(MS1, `${BASE}/wordpressDiscardDeployJob`, {
		websiteId: Number(websiteId),
		jobId: toJobId(jobId),
	});
	return parseDeployResponse(res, "Failed to discard deploy job");
}

export async function overwriteDeployJob(websiteId, jobId) {
	const missing = requireWebsiteId(websiteId);
	if (missing) return missing;
	if (jobId == null || jobId === "") {
		return { ok: false, message: "jobId is required", data: null };
	}
	const res = await PostData(MS1, `${BASE}/wordpressOverwriteDeployJob`, {
		websiteId: Number(websiteId),
		jobId: toJobId(jobId),
	});
	return parseDeployResponse(res, "Failed to overwrite deploy job");
}

/** Check later deploys on the same page before starting a revert preview. */
export async function getRevertImpact(websiteId, jobId) {
	const missing = requireWebsiteId(websiteId);
	if (missing) {
		return {
			...missing,
			canRevert: false,
			laterJobs: [],
			warning: null,
		};
	}
	if (jobId == null || jobId === "") {
		return {
			ok: false,
			message: "jobId is required",
			canRevert: false,
			laterJobs: [],
			warning: null,
			data: null,
		};
	}
	const res = await PostData(MS1, `${BASE}/wordpressGetRevertImpact`, {
		websiteId: Number(websiteId),
		jobId: toJobId(jobId),
	});
	const parsed = parseDeployResponse(res, "Failed to load revert impact");
	if (!parsed.ok) {
		return {
			...parsed,
			canRevert: false,
			laterJobs: [],
			warning: null,
		};
	}
	const data = parsed.data || {};
	const rawLater = Array.isArray(data.later_jobs)
		? data.later_jobs
		: Array.isArray(data.laterJobs)
			? data.laterJobs
			: [];
	return {
		ok: true,
		message: parsed.message,
		canRevert: Boolean(data.can_revert ?? data.canRevert ?? true),
		laterJobs: rawLater.map(normalizeLaterJob).filter(Boolean),
		warning: data.warning || null,
		data,
	};
}

/** Start revert preview pipeline (temp page + verification). */
export async function startRevertDeployJob(websiteId, jobId) {
	const missing = requireWebsiteId(websiteId);
	if (missing) return missing;
	if (jobId == null || jobId === "") {
		return { ok: false, message: "jobId is required", data: null };
	}
	const res = await PostData(MS1, `${BASE}/wordpressRevertDeployJob`, {
		websiteId: Number(websiteId),
		jobId: toJobId(jobId),
	});
	return parseDeployResponse(res, "Failed to start revert preview");
}

export function pollDeployJob(websiteId, jobId, options) {
	return pollDeployJobWith(getDeployJob, websiteId, jobId, options);
}
